/**
 * Trường nhập dùng chung — gắn nhãn, gợi ý và lỗi theo `aria-describedby`
 * (docs/08 §8.7).
 */

import { useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { Icon } from './Icon';
import { useTLa } from '../lib/i18n';

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string | undefined;
  trailing?: ReactNode;
}

export function Field({ label, hint, error, trailing, ...input }: FieldProps) {
  const t = useTLa();
  const id = useId();
  const hintId = `${id}-hint`;
  const errId = `${id}-err`;
  const describedBy = [hint ? hintId : null, error ? errId : null].filter(Boolean).join(' ');

  /*
   * OQ-244 — nút hiện/ẩn mật khẩu.
   *
   * Đặt ở `Field` chứ không ở từng màn: có 15 ô mật khẩu rải khắp mười tệp, và
   * ô thứ mười sáu viết sau sẽ quên. Gom vào đây thì mọi ô có sẵn nút, cùng
   * biểu tượng, cùng phím tắt, cùng nhãn cho trình đọc màn hình.
   *
   * `type` phải đọc từ state chứ không từ `input.type` — nếu không, `{...input}`
   * ở dưới sẽ ghi đè lại `password` và nút bấm không có tác dụng gì.
   */
  const laMatKhau = input.type === 'password';
  const [hien, setHien] = useState(false);
  const kieu = laMatKhau && hien ? 'text' : (input.type ?? 'text');

  return (
    <div className={error ? 'field invalid' : 'field'}>
      <label htmlFor={id}>{label}</label>
      <div className={laMatKhau ? 'o-mat-khau' : undefined}>
        <input
          id={id}
          /*
           * `type` PHẢI có mặt, kể cả khi là "text".
           *
           * `app.css` bắt ô nhập bằng selector liệt kê tường minh —
           * `input[type='text'], input[type='password'], …` — nên một `<input>`
           * không khai `type` **không khớp selector nào** và rơi về style mặc
           * định của trình duyệt: thấp hơn, viền khác, bo góc khác. HTML coi
           * `type` khuyết là `text`, còn CSS thì không: hai cách hiểu lệch nhau.
           *
           * Lỗi này im lặng vì ô vẫn nhập được và vẫn nằm đúng chỗ — chỉ trông
           * khác những ô bên cạnh. Xem OQ-112.
           */
          aria-invalid={error ? true : undefined}
          {...(describedBy ? { 'aria-describedby': describedBy } : {})}
          {...input}
          type={kieu}
        />
        {laMatKhau ? (
          <button
            type="button"
            className="nut-hien-mat-khau"
            /* `tabIndex={-1}`: Tab phải đi từ ô mật khẩu sang nút Gửi, không vướng nút này. */
            tabIndex={-1}
            aria-label={t(hien ? 'common.hidePassword' : 'common.showPassword')}
            title={t(hien ? 'common.hidePassword' : 'common.showPassword')}
            data-od-id="toggle-password"
            onClick={() => setHien((truoc) => !truoc)}
          >
            <Icon name={hien ? 'eye-off' : 'eye'} size={16} />
          </button>
        ) : null}
      </div>
      {trailing}
      {hint ? (
        <span className="hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="err" id={errId}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

export function SelectField({
  label,
  hint,
  error,
  children,
  ...select
}: {
  label: string;
  hint?: string;
  error?: string | undefined;
  children: ReactNode;
} & React.SelectHTMLAttributes<HTMLSelectElement>) {
  const id = useId();
  /*
   * Nối lỗi vào `<select>` y như `Field` làm cho `<input>` — OQ-145.
   *
   * Bản trước vẽ `<span className="err">` mà **không có `id`**, và `<select>`
   * không có `aria-describedby` lẫn `aria-invalid`. Người nhìn thấy chữ đỏ;
   * người dùng trình đọc màn hình nghe tên trường rồi hết — thông báo lỗi là
   * một mẩu chữ mồ côi, không thuộc về control nào.
   *
   * Không công cụ quét tự động nào gắn cờ chuyện này: nhãn có, `id` có, cặp
   * `label/for` hợp lệ. Nó chỉ sai ở chỗ **thiếu mối nối**.
   *
   * `SelectField` đang được dùng kèm `error` ở mười chỗ, trong đó có loại bản
   * ghi DNS và TTL — tức đúng những ô hay nhập sai nhất.
   */
  const hintId = `${id}-hint`;
  const errId = `${id}-err`;
  const describedBy = [hint ? hintId : null, error ? errId : null].filter(Boolean).join(' ');

  return (
    <div className={error ? 'field invalid' : 'field'}>
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        aria-invalid={error ? true : undefined}
        {...(describedBy ? { 'aria-describedby': describedBy } : {})}
        {...select}
      >
        {children}
      </select>
      {hint ? (
        <span className="hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="err" id={errId}>
          {error}
        </span>
      ) : null}
    </div>
  );
}
